import { IDeviceOptions } from "app/paywalls/helpers/types"
import classNames from "classnames"
import { CSSProperties, FC, PropsWithChildren } from "react"
import useIosFrame from "./useIosFrame"

export const rgba2hex = (orig) => {
  let a,
    rgb = orig.replace(/\s/g, "").match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i),
    alpha = ((rgb && rgb[4]) || "").trim(),
    hex = rgb
      ? (rgb[1] | (1 << 8)).toString(16).slice(1) +
        (rgb[2] | (1 << 8)).toString(16).slice(1) +
        (rgb[3] | (1 << 8)).toString(16).slice(1)
      : orig

  if (alpha !== "") {
    a = alpha
  } else {
    a = 0o1
  }
  // multiply before convert to HEX
  a = ((a * 255) | (1 << 8)).toString(16).slice(1)
  hex = hex + a

  return hex
}

const Loader = ({ isLightBackground }: { isLightBackground: boolean }) => {
  return (
    <div className={"absolute z-[-1] flex flex-row items-center justify-center w-full h-full"}>
      <svg
        role="status"
        className={classNames(
          "inline w-14",
          "h-14",
          "mr-2",
          isLightBackground ? "text-gray-300" : "text-gray-600",
          "animate-spin",
          isLightBackground ? "fill-black" : "fill-gray-200"
        )}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    </div>
  )
}

const StatusBar = (props: {
  contentWidth: number
  notchWidth: number
  notchHeight: number
  isLightBackground: boolean
}) => {
  const { contentWidth = DEFAULT_CONTENT_WIDTH, notchWidth, notchHeight, isLightBackground } = props
  const statusBarTextColor = isLightBackground ? "black" : "white"

  const containerStyles: CSSProperties = {
    position: "absolute",
    width: `${(contentWidth - notchWidth) / 2}px`,
    height: `${notchHeight}px`,
    color: statusBarTextColor,
  }

  return (
    <>
      {/* STATUS BAR LEFT */}
      <div
        className={"flex flex-row items-end content-center justify-center"}
        style={{
          ...containerStyles,
          left: `6px`,
          top: 1,
          fontWeight: 500,
          fontSize: "0.94rem",
          color: statusBarTextColor,
        }}
      >
        <p>
          {new Date()
            .toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })
            .replace(" AM", "")
            .replace(" PM", "")}
        </p>
      </div>

      {/* STATUS BAR RIGHT */}
      <div
        className={"flex flex-row items-end content-center justify-center"}
        style={{
          ...containerStyles,
          right: `6px`,
          top: 9,
        }}
      >
        <svg
          width={(contentWidth - notchWidth) / 3.3}
          height="39"
          viewBox="0 0 237 39"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect y="23" width="10" height="14" rx="2" fill={statusBarTextColor} />
          <rect x="16" y="16" width="10" height="21" rx="2" fill={statusBarTextColor} />
          <rect x="32" y="8" width="10" height="29" rx="2" fill={statusBarTextColor} />
          <rect x="48" width="10" height="37" rx="2" fill={statusBarTextColor} />
          <path
            d="M74 28.7958C74 30.1204 74.7893 31.538 76.2518 32.8859C78.3412 34.8613 81.9859 36 86.095 36C93.663 36 98.4685 32.1655 98.4685 26.1232C98.4685 21.4521 95.7292 18.6401 89.9254 17.3155L85.1664 16.1768C82.2181 15.5028 80.7788 14.1085 80.7788 12.1331C80.7788 9.66972 82.8913 8.08944 86.1878 8.08944C88.9272 8.08944 91.063 9.27465 92.4791 11.3894C93.2219 12.319 94.0577 12.7373 95.0327 12.7373C96.5417 12.7373 97.5631 11.738 97.5631 10.2739C97.5631 8.80986 96.797 7.32254 95.3577 6.02113C93.2684 4.09225 89.879 3 86.095 3C79.0376 3 74.4411 6.78803 74.4411 12.6211C74.4411 17.2225 77.1572 20.1275 82.6127 21.3824L87.3486 22.5444C90.7844 23.3577 92.2701 24.6824 92.2701 26.8204C92.2701 29.4 90.1576 30.8873 86.4432 30.8873C83.6806 30.8873 81.3127 29.8183 79.5251 27.75C78.4108 26.5416 77.5519 26.1 76.5072 26.1C75.0911 26.1 74 27.2155 74 28.7958Z"
            fill={statusBarTextColor}
          />
          <path
            d="M107.871 32.0958C108.59 34.6521 109.751 35.4655 111.655 35.4655C113.581 35.4655 114.835 34.3732 115.439 32.0958L120.407 13.1556H120.662L125.63 31.9563C126.28 34.443 127.51 35.4655 129.53 35.4655C131.457 35.4655 132.525 34.5127 133.198 32.0958L139.768 8.25211C139.907 7.71761 140 6.88099 140 6.34648C140 4.55704 138.723 3.30211 136.889 3.30211C135.171 3.30211 134.08 4.48732 133.569 6.83451L129.159 26.6345H128.857L124.028 6.81127C123.448 4.41761 122.403 3.30211 120.523 3.30211C118.665 3.30211 117.644 4.37113 117.064 6.81127L112.235 26.6345H111.933L107.406 6.83451C106.872 4.44085 105.828 3.30211 104.133 3.30211C102.276 3.30211 100.976 4.58028 100.976 6.39296C100.976 6.90423 101.092 7.76408 101.301 8.55423L107.871 32.0958Z"
            fill={statusBarTextColor}
          />
          <rect x="161" y="6" width="52" height="27" rx="4" fill={statusBarTextColor} />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M165 0C159.477 0 155 4.47715 155 10V29C155 34.5228 159.477 39 165 39H220C225.523 39 230 34.5228 230 29V10C230 4.47715 225.523 0 220 0H165ZM165 3C161.134 3 158 6.13401 158 10V29C158 32.866 161.134 36 165 36H220C223.866 36 227 32.866 227 29V10C227 6.13401 223.866 3 220 3H165Z"
            fill={statusBarTextColor}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M233 26.2699C235.379 25.0124 237 22.513 237 19.6349C237 16.7569 235.379 14.2575 233 13V26.2699Z"
            fill={statusBarTextColor}
          />
        </svg>
      </div>
    </>
  )
}

const NotchRelief = ({
  notchWidth,
  notchReliefWidth,
  notchReliefXOffset,
  deviceColor,
  isErrorState,
}: {
  notchWidth: number
  notchReliefWidth: number
  notchReliefXOffset: number
  deviceColor: string
  isErrorState: boolean
}) => {
  return (
    <>
      {/* NOTCH TOP LEFT RELIEF */}
      <div
        style={{
          position: "absolute",
          left: `${notchReliefXOffset}px`,
          top: -1,
        }}
      >
        <svg
          width={notchReliefWidth + 1}
          height={notchReliefWidth + 1}
          viewBox="0 0 50 50"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M50 50C50 22.3857 27.6142 0 0 0H50V50Z"
            fill={isErrorState ? "rgb(185 28 28)" : deviceColor}
          />
        </svg>
      </div>

      {/* NOTCH TOP RIGHT RELIEF */}
      <div
        style={{
          position: "absolute",
          left: `${notchReliefXOffset + notchWidth + notchReliefWidth - 1}px`,
          top: -1,
          transform: "scaleX(-1)",
        }}
      >
        <svg
          width={notchReliefWidth + 1}
          height={notchReliefWidth + 1}
          viewBox="0 0 50 50"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M50 50C50 22.3857 27.6142 0 0 0H50V50Z"
            fill={isErrorState ? "rgb(185 28 28)" : deviceColor}
          />
        </svg>
      </div>
    </>
  )
}

const HomeBar = (props) => {
  const { contentWidth, contentHeight, isPortrait } = props

  const width = (500 / 1170) * contentWidth
  const height = (15 / 2532) * contentHeight
  const left = isPortrait ? (contentWidth - width) / 2 : (contentHeight - width) / 2
  const bottom = (24 / 2532) * contentHeight

  const homeBarStyles: CSSProperties = {
    position: "absolute",
    bottom: `${bottom}px`,
    left: `${left}px`,
    borderRadius: `${height / 2}px`,
    backdropFilter: "invert(1) grayscale(1) contrast(100)",
    WebkitBackdropFilter: "invert(1) grayscale(1) contrast(100)",
    width: `${width}px`,
    height: `${height}px`,
  }
  return <div style={homeBarStyles}></div>
}

type TIOSProps = {
  backgroundColor?: string
  /**
   * border color of the iframe
   * @default "#44494E"
   */
  deviceColor?: string
  /**
   * scale of the paywall preview
   * @default 1.0
   */
  scale?: number
  /**
   * background color the paywall preview
   */
  maxWidth?: number
  /**
   * changes the preview to either show the modal or not
   * @deafult false
   */
  isModal?: boolean
  /**
   * forces a reload of the iframe
   * https://stackoverflow.com/questions/48830316/how-do-you-reload-an-iframe-with-reactjs
   */
  sticky?: boolean
  skipBorder?: boolean
  hasError?: boolean
  showLoader?: boolean
  orientation?: "portrait" | "landscape"
}

const DEFAULT_CONTENT_WIDTH = 375

const IOS: FC<PropsWithChildren<IDeviceOptions & TIOSProps>> = (props) => {
  const {
    children,
    backgroundColor = "black",
    deviceColor = "#44494E",
    scale = 1.0,
    maxWidth,
    contentWidth = DEFAULT_CONTENT_WIDTH,
    aspectRatio = 19.5 / 9,
    contentRadius = 40,
    bezelWidth = 15,
    showNotch = false,
    isModal = false,
    sticky = false,
    skipBorder = false,
    hasError = false,
    showLoader = true,
    orientation = "portrait",
  } = props
  const isErrorState = hasError

  const bezelX = bezelWidth
  const contentHeight = contentWidth * aspectRatio
  const baseDeviceWidth = contentWidth + bezelX * 2
  const trueScale =
    maxWidth != null && baseDeviceWidth * scale > maxWidth ? maxWidth / baseDeviceWidth : scale
  const showModal = isModal && showNotch

  const {
    isLightBackground,
    borderWidth,
    deviceRadius,
    notchHeight,
    notchWidth,
    deviceWidth,
    deviceHeight,
    notchXOffset,
    notchRadius,
    notchReliefWidth,
    notchReliefXOffset,
    modalCornerRadius,
    modalOffset,
    modalBackgroundWidth,
  } = useIosFrame({
    contentWidth,
    contentHeight,
    contentRadius,
    aspectRatio,
    bezelX,
    scale,
    trueScale,
    backgroundColor,
  })

  const isPortrait = orientation === "portrait"

  const modalBackgroundColor = isLightBackground ? "white" : "black"
  const modalUnderlayBackgroundColor = isLightBackground
    ? "rgba(0,0,0,0.3)"
    : "rgba(255,255,255,0.2)"

  const outerContainerStyles: CSSProperties = {
    position: "relative",
    width: isPortrait ? deviceWidth : deviceHeight,
    height: isPortrait ? deviceHeight : deviceWidth,
    WebkitMaskImage: "-webkit-radial-gradient(white, black)",
    borderRadius: `${(deviceRadius + borderWidth) * trueScale}px`,
    borderWidth: borderWidth,
    borderColor: isErrorState ? "rgb(185 28 28)" : deviceColor,
    overflow: "hidden",
  }

  const innerContainerStyles: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    position: "relative",
    transform: `scale(${trueScale}, ${trueScale})`,
    transformOrigin: "top left",
    backgroundColor: modalBackgroundColor,
    width: isPortrait ? contentWidth : contentWidth * aspectRatio,
    height: isPortrait ? contentWidth * aspectRatio : contentWidth,
  }

  const statusBarAndNotchContainerStyles: CSSProperties = {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    position: "absolute",
    top: isPortrait ? "0px" : "100%",
    left: "0px",
    transform: isPortrait ? "rotate(0deg)" : "rotate(-90deg)",
    transformOrigin: isPortrait ? "center" : "top left",
    width: isPortrait ? "100%" : `${contentWidth}px`,
  }

  const notchStyles: CSSProperties = {
    position: "absolute",
    left: `${notchXOffset}px`,
    top: "-5px",
    transform: "rotate(0deg)",
    zIndex: 10,
    borderBottomLeftRadius: `${notchRadius}px`,
    borderBottomRightRadius: `${notchRadius}px`,
    backgroundColor: isErrorState ? "rgb(185 28 28)" : deviceColor,
    width: `${notchWidth}px`,
    height: `${notchHeight + 5}px`,
  }

  const modalUnderlayStyles: CSSProperties = {
    position: "relative",
    top: isPortrait ? "0px" : `calc(${contentHeight / 2 - notchHeight * 2}px + 7px)`,
    right: isPortrait ? "0px" : `calc(-50% + ${modalOffset - notchHeight / 2}px + 10px)`,
    transition: showModal ? "margin-bottom 0.7s ease" : "margin-bottom 0.2s ease",
    transform: isPortrait ? "rotate(0deg)" : "rotate(90deg)",
    marginBottom: showModal ? "0px" : `${100}px`,
    borderTopLeftRadius: `${modalCornerRadius * 2}px`,
    borderTopRightRadius: `${modalCornerRadius * 2}px`,
    backgroundColor: showModal ? modalUnderlayBackgroundColor : "clear",
    // boxShadow: "inset 0 -5px 5px rgba(0,0,0,0.1)",
    width: `${isPortrait ? modalBackgroundWidth : 600}px`,
    height: `${(modalOffset - notchHeight) / 2}px`,
  }

  if (skipBorder) {
    return <>{children}</>
  }

  return (
    <div
      onMouseEnter={() => {
        document.body.style.setProperty("overscroll-behavior-x", "none")
      }}
      onMouseLeave={() => {
        document.body.style.setProperty("overscroll-behavior-x", "auto")
      }}
      className={classNames(sticky ? "sticky top-9" : "")}
      style={outerContainerStyles}
    >
      <div style={innerContainerStyles}>
        {/* LOADING INDICATOR */}
        {showLoader && <Loader isLightBackground={isLightBackground} />}

        {showNotch && (
          <div style={statusBarAndNotchContainerStyles}>
            {/* NOTCH */}
            <div style={notchStyles}></div>

            {orientation === "portrait" && (
              <StatusBar
                contentWidth={contentWidth}
                notchWidth={notchWidth}
                notchHeight={notchHeight}
                isLightBackground={isLightBackground}
              />
            )}

            <NotchRelief
              notchWidth={notchWidth}
              notchReliefWidth={notchReliefWidth}
              notchReliefXOffset={notchReliefXOffset}
              deviceColor={deviceColor}
              isErrorState={isErrorState}
            />

            {/* MODAL UNDERLAY */}
            {showModal && (
              <div
                className={"flex flex-row items-end content-center justify-center"}
                style={{
                  transition: "height 0.4s ease",
                  width: "100%",
                  height: showModal ? `${modalOffset}px` : "0px",
                }}
              >
                <div style={modalUnderlayStyles}></div>
              </div>
            )}
          </div>
        )}

        {!showModal && (
          <HomeBar
            contentWidth={contentWidth}
            contentHeight={contentHeight}
            isPortrait={isPortrait}
          />
        )}

        {children}
      </div>
    </div>
  )
}

export default IOS
